import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { X, Check, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const CursorToastContext = createContext(null);

// Toast types with their styles - all use white bg with beige border and blue text
const TOAST_STYLES = {
  success: {
    icon: Check,
    iconColor: 'text-emerald-600',
  },
  error: {
    icon: AlertCircle,
    iconColor: 'text-red-500',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-500',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
  },
};

const TOAST_DURATION = 7000; // 7 seconds
const FADE_OUT_DURATION = 500; // 0.5 second fade out

export function CursorToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const toastIdCounter = useRef(0);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Add a toast
  const MAX_TOASTS = 3;
  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastIdCounter.current;

    setToasts((prev) => {
      // Dedup: skip if identical non-fading toast already visible
      const duplicate = prev.find(t => t.message === message && t.type === type && !t.fading);
      if (duplicate) return prev;
      // Cap: if at limit, drop the oldest (first in array)
      const trimmed = prev.length >= MAX_TOASTS ? prev.slice(1) : prev;
      return [...trimmed, { id, message, type, createdAt: Date.now(), fading: false }];
    });

    // Start fade out before removal
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => t.id === id ? { ...t, fading: true } : t));
    }, TOAST_DURATION - FADE_OUT_DURATION);

    // Remove after fade completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DURATION);

    return id;
  }, []);

  // Remove a toast with fade out
  const removeToast = useCallback((id) => {
    // Start fade out
    setToasts((prev) => prev.map((t) => t.id === id ? { ...t, fading: true } : t));
    
    // Remove after fade completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, FADE_OUT_DURATION);
  }, []);

  // Right-click anywhere to dismiss all toasts
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (toasts.length > 0 && !toasts.every(t => t.fading)) {
        e.preventDefault();
        // Fade out all toasts
        setToasts((prev) => prev.map((t) => ({ ...t, fading: true })));
        // Remove all after fade
        setTimeout(() => {
          setToasts([]);
        }, FADE_OUT_DURATION);
      }
    };

    window.addEventListener('contextmenu', handleContextMenu);
    return () => window.removeEventListener('contextmenu', handleContextMenu);
  }, [toasts]);

  // Toast API matching sonner's interface
  const toast = useCallback((message) => addToast(message, 'info'), [addToast]);
  toast.success = useCallback((message) => addToast(message, 'success'), [addToast]);
  toast.error = useCallback((message) => addToast(message, 'error'), [addToast]);
  toast.info = useCallback((message) => addToast(message, 'info'), [addToast]);
  toast.warning = useCallback((message) => addToast(message, 'warning'), [addToast]);

  return (
    <CursorToastContext.Provider value={{ toast, toasts, mousePos, removeToast }}>
      {children}
      <CursorToastRenderer />
    </CursorToastContext.Provider>
  );
}

// The actual toast renderer that follows the cursor
function CursorToastRenderer() {
  const context = useContext(CursorToastContext);
  if (!context) return null;

  const { toasts, mousePos, removeToast } = context;

  if (toasts.length === 0) return null;

  // Calculate position - offset from cursor
  const offsetX = 15;
  const offsetY = 20;

  // Ensure toasts don't go off-screen
  const maxX = typeof window !== 'undefined' ? window.innerWidth - 250 : 1000;
  const maxY = typeof window !== 'undefined' ? window.innerHeight - 100 : 800;

  const posX = Math.min(mousePos.x + offsetX, maxX);
  const posY = Math.min(mousePos.y + offsetY, maxY);

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div
        className="fixed pointer-events-none z-[9999]"
        style={{
          left: posX,
          top: posY,
          transition: 'left 0.05s ease-out, top 0.05s ease-out',
        }}
      >
      <div className="flex flex-col gap-2">
        {toasts.map((toast, index) => {
          const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
          const Icon = style.icon;

          return (
            <div
              key={toast.id}
              className={`
                pointer-events-auto
                flex items-start gap-2 px-3 py-2
                bg-white border-2 border-[#C9956C]
                text-[#1E3A5F]
                rounded-lg shadow-lg
                text-sm font-medium
                max-w-[300px]
                transition-opacity duration-500
                ${toast.fading ? 'opacity-0' : 'opacity-100'}
              `}
              style={{
                animation: toast.fading ? 'none' : `slideIn 0.2s ease-out ${index * 50}ms both`,
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                removeToast(toast.id);
              }}
            >
              <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.iconColor}`} />
              <span className="text-xs leading-tight">{toast.message}</span>
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
}

// Hook to use the toast
export function useCursorToast() {
  const context = useContext(CursorToastContext);
  if (!context) {
    throw new Error('useCursorToast must be used within a CursorToastProvider');
  }
  return context.toast;
}

// Export a standalone toast function for non-component usage
let globalToast = null;

export function setGlobalToast(toastFn) {
  globalToast = toastFn;
}

export const toast = {
  success: (msg) => globalToast?.success(msg),
  error: (msg) => globalToast?.error(msg),
  info: (msg) => globalToast?.info(msg),
  warning: (msg) => globalToast?.warning(msg),
};

// Bridge component to connect context to global toast
export function CursorToastBridge() {
  const toastFn = useCursorToast();
  
  useEffect(() => {
    setGlobalToast(toastFn);
  }, [toastFn]);

  return null;
}

export default CursorToastProvider;
